import React, { useState, useRef, useEffect } from "react";
import { apiCall } from "@/api/apiCall";
import { toast } from "sonner";
import { useApiQueue } from "@/hooks/useApiQueue";
import CarPark from "@/assets/img/TaifContract-carPark.png";
import OnStreet from "@/assets/img/TaifContract-onStreet.png";
import Enforcement from "@/assets/img/TaifContract-Enforcement.png";
import { AssetsPanel } from "../../components/shared/AssetsPanel";
import { API_CALL_TIME } from "@/constants/constant";
import contractLogo from "@/assets/img/capital_parking.png"
// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════
const groupsToFlat = (groups = []) => {
  const result = [];
  groups.forEach((g) => {
    result.push({
      name: g.group_name || "",
      isCategory: true,
      subCount: "",
      totalCount: String(g.group_total_qty ?? "-"),
    });
    (g.items || []).forEach((item) => {
      result.push({
        name: item.item_name || "",
        isCategory: false,
        subCount: String(item.qty ?? "-"),
        totalCount: "",
      });
    });
  });
  return result;
};

const flatToGroups = (flat) => {
  const groups = [];
  let cur = null;
  flat.forEach((a) => {
    if (a.isCategory) {
      if (cur) groups.push(cur);
      cur = { group_name: a.name, group_total_qty: a.totalCount || "", items: [] };
    } else if (cur) {
      cur.items.push({ item_name: a.name, qty: a.subCount || "" });
    }
  });
  if (cur) groups.push(cur);
  return groups;
};

// ═══════════════════════════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════════════════════════
const CAPITAL_PARKING_PROFILE_CONFIG = {
  capital_parking_on_street_profile: {
    profileKey: "capital_parking_on_street_profile",
    violationKey: "capital_parking_on_street_violations",
    vehicleWeightKey: "capital_parking_on_street_vehicle_weight",
    assetsKey: "capital_parking_on_street_assets",

    profileFallback: {
      table_key: "capital_parking_on_street_profile",
      title: "Capital Parking On Street - Profile",
      data: {
        total_bays: { label: "Capital Parking On Street Parking Bays", value: "0" },
        contract_term: { value: "20 years" },
        contract_period: { start_date: "26.07.2026", end_date: "25.07.2046" },
        parking_violation: {
          value: "Parking and Violation charges are as per below violation classification table",
        },
      },
    },

    violationFallback: {
      table_key: "capital_parking_on_street_violations",
      title: "Violation classification table",
      data: {
        subtitle:
          "The classification of parking violations in commercial areas and public spaces is based on Cabinet Resolution No. (550) dated 1442/09/15 AH",
        rows: [
          {
            group: "Group 1 Violations",
            item: "1/1",
            violation_type: "Using a parking spot without paying the required fee",
            penalty_amount: "200",
            penalty_action: "Vehicle towing and impoundment",
          },
          {
            group: "",
            item: "1/2",
            violation_type: "Exceeding the paid parking time without vacating the spot",
            penalty_amount: "100",
            penalty_action: "Paying the additional charge",
          },
          {
            group: "",
            item: "1/3",
            violation_type: "Exceeding the maximum allowed parking duration",
            penalty_amount: "100",
            penalty_action: "Vehicle towing and impoundment",
          },
          {
            group: "",
            item: "1/4",
            violation_type: "Incorrect parking within a designated spot",
            penalty_amount: "100",
            penalty_action: "-",
          },
          {
            group: "Group 2 Violations",
            item: "2/1",
            violation_type: "Parking in a space reserved for specific use",
            penalty_amount: "300",
            penalty_action: "Vehicle towing and impoundment",
          },
          {
            group: "",
            item: "2/2",
            violation_type: "Parking in a prohibited area",
            penalty_amount: "300",
            penalty_action: "Vehicle towing and impoundment",
          },
          {
            group: "Group 3 Violations",
            item: "3/1",
            violation_type:
              "Placing barriers, obstacles, fencing, or blocking a parking space without permission",
            penalty_amount: "400",
            penalty_action: "Removal cost charged to the violator",
          },
          {
            group: "Group 4 Violations",
            item: "4/1",
            violation_type: "Parking in areas designated for emergency exits",
            penalty_amount: "900",
            penalty_action: "Vehicle towing and impoundment",
          },
          {
            group: "",
            item: "4/2",
            violation_type: "Parking at the entrances or exits of parking facilities",
            penalty_amount: "500",
            penalty_action: "Vehicle towing and impoundment",
          },
        ],
      },
    },

    vehicleWeightFallback: {
      table_key: "capital_parking_on_street_vehicle_weight",
      title: "Vehicle Weight",
      data: {
        rows: [
          { weight_class: "Light vehicles (up to 3 tons)", towing_fee: "⃁ 250 per day", impound_fee: "⃁ 20 per day" },
          {
            weight_class: "Heavy vehicles (over 3 tons, up to 5 tons)",
            towing_fee: "⃁ 1,250 per day",
            impound_fee: "⃁ 50 per day",
          },
        ],
      },
    },

    assetsFallback: {
      table_key: "capital_parking_on_street_assets",
      title: "Capital Parking On-Street Profile",
      data: {
        groups: [
          {
            group_name: "Ticket Dispenser",
            group_total_qty: "0",
            items: [
              { item_name: "DIP G100", qty: "0" },
              { item_name: "G100 64 Bit", qty: "0" },
              { item_name: "G100 32 Bit", qty: "0" },
            ],
          },
          {
            group_name: "Ticket Verifier",
            group_total_qty: "0",
            items: [
              { item_name: "EV DIP G100", qty: "0" },
              { item_name: "EV G100 64 Bit", qty: "0" },
              { item_name: "EV G100 32 Bit", qty: "0" },
            ],
          },
          {
            group_name: "APM's",
            group_total_qty: "0",
            items: [
              { item_name: "APM ETS-70", qty: "0" },
              { item_name: "APM ETS-100", qty: "0" },
            ],
          },
          {
            group_name: "PIN Pad",
            group_total_qty: "0",
            items: [{ item_name: "IUP250", qty: "0" }],
          },
          {
            group_name: "Card Reader",
            group_total_qty: "0",
            items: [{ item_name: "IUR250", qty: "0" }],
          },
          {
            group_name: "B-Contactless",
            group_total_qty: "0",
            items: [{ item_name: "IUC150", qty: "0" }],
          },
          {
            group_name: "Banking All-in-One",
            group_total_qty: "248",
            items: [{ item_name: "IUC150", qty: "248" }],
          },
          { group_name: "RFID Reader", group_total_qty: "0", items: [] },
          { group_name: "IP Barrier", group_total_qty: "0", items: [] },
          {
            group_name: "ANPR Camera",
            group_total_qty: "0",
            items: [
              { item_name: "ARH", qty: "0" },
              { item_name: "Vidar", qty: "0" },
              { item_name: "Luminera", qty: "0" },
            ],
          },
          {
            group_name: "Sliding Gate",
            group_total_qty: "0",
            items: [
              { item_name: "RIGHT", qty: "0" },
              { item_name: "LEFT", qty: "0" },
            ],
          },
          {
            group_name: "PND's",
            group_total_qty: "248",
            items: [
              { item_name: "TPAL- BNA Large", qty: "0" },
              { item_name: "T-PAL TX", qty: "0" },
              { item_name: "T-PAL V2", qty: "0" },
              { item_name: "STREET SMART BNA GEN3", qty: "248" },
              { item_name: "STREET SMART TX GEN3", qty: "0" },
            ],
          },
          {
            group_name: "Pound Cahier Device",
            group_total_qty: "0",
            items: [{ item_name: "CN-50", qty: "0" }],
          },
          {
            group_name: "Driver Device",
            group_total_qty: "0",
            items: [{ item_name: "CN-51", qty: "0" }],
          },
          {
            group_name: "Enf Device",
            group_total_qty: "0",
            items: [
              { item_name: "Oukitel WP2", qty: "0" },
              { item_name: "Doogee V10", qty: "0" },
            ],
          },
          {
            group_name: "Validation",
            group_total_qty: "0",
            items: [{ item_name: "Lenova Tablet", qty: "0" }],
          },
          {
            group_name: "Printer",
            group_total_qty: "0",
            items: [
              { item_name: "PR-3", qty: "0" },
              { item_name: "SEWOO", qty: "0" },
              { item_name: "BIXOLON", qty: "0" },
            ],
          },
          { group_name: "Tow Truck", group_total_qty: "0", items: [] },
          { group_name: "Clamp Vehicle", group_total_qty: "0", items: [] },
          { group_name: "Support Vehicle", group_total_qty: "1", items: [] },
        ],
      },
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const CapitalParkingOnStreet = ({ page, year = "", month = "", monthNumber = "", contract = "" }) => {
  const cfg = CAPITAL_PARKING_PROFILE_CONFIG[page?.type];
  const _cfg = cfg || CAPITAL_PARKING_PROFILE_CONFIG["capital_parking_on_street_profile"];

  const hasFetchedRef = useRef(false);
  const saveRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [profileTable, setProfileTable] = useState(_cfg.profileFallback);
  const [violationTable, setViolationTable] = useState(_cfg.violationFallback);
  const [vehicleWeightTable, setVehicleWeightTable] = useState(_cfg.vehicleWeightFallback);
  const [assetsTable, setAssetsTable] = useState(_cfg.assetsFallback);
  const [parkingBaysData, setParkingBaysData] = useState(null);

  const { enqueue } = useApiQueue();

  const effectiveTotalBays =
    parkingBaysData?.total_bays ?? profileTable.data.total_bays?.value ?? "0";

  const profileRef = useRef(null);
  const violationRef = useRef(null);
  const vehicleWeightRef = useRef(null);
  const assetsRef = useRef(null);

  useEffect(() => {
    profileRef.current = profileTable;
  }, [profileTable]);
  useEffect(() => {
    violationRef.current = violationTable;
  }, [violationTable]);
  useEffect(() => {
    vehicleWeightRef.current = vehicleWeightTable;
  }, [vehicleWeightTable]);
  useEffect(() => {
    assetsRef.current = assetsTable;
  }, [assetsTable]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    (async () => {
      try {
        const [tablesRes, baysRes] = await Promise.all([
          enqueue(() =>
            apiCall("GET", "/boardpack/get-tables-data", { year, contract, month: monthNumber })
          ),
          enqueue(() =>
            apiCall("GET", "/boardpack/parking/bays", {
              contract: "2010001",
              year,
              month,
              device_onstreet: "RYDO",
              type: "on_street",
            })
          ),
        ]);
        if (tablesRes?.success) {
          const data = tablesRes?.data || [];
          const find = (key, fb) => data.find((t) => t.table_key === key) || fb;
          setProfileTable(find(cfg.profileKey, cfg.profileFallback));
          setViolationTable(find(cfg.violationKey, cfg.violationFallback));
          setVehicleWeightTable(find(cfg.vehicleWeightKey, cfg.vehicleWeightFallback));
          setAssetsTable(find(cfg.assetsKey, cfg.assetsFallback));
        }
        if (baysRes?.success && baysRes?.data) setParkingBaysData(baysRes.data);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    })();
  }, [year, month, monthNumber, contract]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoSave = () => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const tables = [
        {
          table_key: profileRef.current?.table_key,
          title: profileRef.current?.title,
          data: profileRef.current?.data,
        },
        {
          table_key: violationRef.current?.table_key,
          title: violationRef.current?.title,
          data: violationRef.current?.data,
        },
        {
          table_key: vehicleWeightRef.current?.table_key,
          title: vehicleWeightRef.current?.title,
          data: vehicleWeightRef.current?.data,
        },
        {
          table_key: assetsRef.current?.table_key,
          title: assetsRef.current?.title,
          data: assetsRef.current?.data,
        },
      ];
      const res = await apiCall("POST", "/boardpack/add-tables-dataSS", {
        contract,
        year,
        month: monthNumber,
        created_by: 1,
        tables,
      });
      if (!res?.success) throw new Error(res?.message || "Save failed");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────
  const patchProfile = (field, subField, value) => {
    setProfileTable((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: subField ? { ...prev.data[field], [subField]: value } : value,
      },
    }));
    autoSave();
  };

  const handleViolationCell = (rIdx, key, value) => {
    setViolationTable((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((r, i) => (i === rIdx ? { ...r, [key]: value } : r)),
      },
    }));
    autoSave();
  };

  const handleVehicleWeightCell = (rIdx, key, value) => {
    setVehicleWeightTable((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((r, i) => (i === rIdx ? { ...r, [key]: value } : r)),
      },
    }));
    autoSave();
  };

  const handleAssetSubCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current?.data?.groups || []).map((a, i) =>
      i === flatIdx ? { ...a, subCount: value } : a
    );
    setAssetsTable((prev) => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  const handleAssetTotalCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current?.data?.groups || []).map((a, i) =>
      i === flatIdx ? { ...a, totalCount: value } : a
    );
    setAssetsTable((prev) => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  // ── Derived ───────────────────────────────────────────────────────────
  if (!cfg) return null;

  const pd = profileTable.data;
  const flatAssets = groupsToFlat(assetsTable.data.groups);

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen p-1 bg-white">
      {/* ── Header ── */}
     <div className="flex justify-between items-center mb-3">
             <div className="ml-2">
               <img
                 src={contractLogo}
                 alt="Bab Al Ree Logo"
                 style={{ height: "30px", objectFit: "contain" }}
               />
             </div>
             <h1 className="text-xl font-bold flex-1 text-center">{profileTable.title}</h1>
           </div>

      <div className="flex flex-row gap-0 !border-2 !border-black p-3">
        {/* ── LEFT ── */}
        <div className="flex-1 pr-2" style={{ zoom: "90%" }}>
          {/* Activities icons */}
          <div className="mb-4">
            <p className="text-[16px] font-bold mb-3">Activities:</p>
            <div className="flex gap-0 justify-start ml-16">
              {["On Street", "Car Park", "Enforcement"].map((label) => (
                <div key={label} className="flex flex-col items-center gap-2">
                  {label === "On Street" && (
                    <img
                      src={OnStreet}
                      alt="On Street"
                      style={{ height: "140px", objectFit: "contain" }}
                    />
                  )}
                  {label === "Car Park" && (
                    <img
                      src={CarPark}
                      alt="Car Park"
                      style={{ height: "150px", objectFit: "contain" }}
                    />
                  )}
                  {label === "Enforcement" && (
                    <img
                      src={Enforcement}
                      alt="Enforcement"
                      style={{ height: "140px", objectFit: "contain" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total Bays — read-only from API */}
          <div className="flex items-center gap-2 mb-1 ml-2">
            <div className="w-10 h-8 bg-gray-100 rounded flex items-center justify-center">
              <img src={CarPark} alt="Car Park" style={{ height: "50px", objectFit: "contain" }} />
            </div>
            <span className="text-[12px]">
              {pd.total_bays?.label}{" "}
              <strong>
                <input
                  type="text"
                  value={effectiveTotalBays}
                  readOnly
                  disabled
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-16 cursor-default"
                />
              </strong>
            </span>
          </div>

          {/* Contract Term */}
          <div className="flex items-center gap-2 mb-1 ml-8">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px] flex items-center gap-1 whitespace-nowrap">
              Contract term{" "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_term?.value || ""}
                  onChange={(e) => patchProfile("contract_term", "value", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-14"
                />
              </strong>
              ; start date{" "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_period?.start_date || ""}
                  onChange={(e) => patchProfile("contract_period", "start_date", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-18"
                />
              </strong>
              {" to "}
              <strong>
                <input
                  type="text"
                  value={pd.contract_period?.end_date || ""}
                  onChange={(e) => patchProfile("contract_period", "end_date", e.target.value)}
                  className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20"
                />
              </strong>
            </span>
          </div>

          {/* Parking Violation Note */}
          <div className="flex items-center gap-2 mb-1 ml-8">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              <input
                type="text"
                value={pd.parking_violation?.value || ""}
                onChange={(e) => patchProfile("parking_violation", "value", e.target.value)}
                className="outline-none bg-transparent !border-0 text-[12px] w-80 font-semibold"
              />
            </span>
          </div>

          {/* Assets Deployed */}
          <div className="flex items-center gap-2 mb-3 ml-8">
            <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-[12px]">Assets Deployed</span>
          </div>

          {/* ── Violation classification table ── */}
          <div className="mb-4">
           <div className="flex flex-col items-center "> <h2 className="font-bold text-[14px] mb-1">{violationTable.title}</h2>
            <p className="text-[12px] text-gray-500 mb-2 max-w-[800px]">
              {violationTable.data.subtitle}
            </p></div>
            <table className="!border-collapse text-[12px] w-[800px] mx-auto">
              <thead>
                <tr className="bg-yellow-200">
                  {["Item", "Violation Type", "Penalty", "Penalty"].map((h, i) => (
                    <th
                      key={i}
                      className="!border !border-gray-400 px-1.5 py-1 font-bold text-center"
                      style={{ width: i === 0 ? "35px" : i === 1 ? "400px" : i === 3 ? "260px" : "80px" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {violationTable.data.rows.map((row, rIdx) => (
                  <React.Fragment key={rIdx}>
                    {row.group && (
                      <tr>
                        <td
                          colSpan={4}
                          className="!border !border-gray-400 px-1 py-0.5 text-center bg-yellow-50"
                        >
                          <input
                            type="text"
                            value={row.group}
                            onChange={(e) => handleViolationCell(rIdx, "group", e.target.value)}
                            className="!border-0 bg-transparent outline-none text-[9px] font-semibold text-center w-full"
                          />
                        </td>
                      </tr>
                    )}
                    <tr className="bg-white">
                      <td className="!border !border-gray-400 px-1 py-0.5 text-center">
                        <input
                          type="text"
                          value={row.item || ""}
                          onChange={(e) => handleViolationCell(rIdx, "item", e.target.value)}
                          className="!border-0 bg-transparent outline-none text-center w-full"
                        />
                      </td>
                      <td className="!border !border-gray-400 px-1 py-0.5">
                        <textarea
                          value={row.violation_type || ""}
                          onChange={(e) =>
                            handleViolationCell(rIdx, "violation_type", e.target.value)
                          }
                          rows={1}
                          className="!border-0 bg-transparent outline-none resize-none w-full text-[12px]"
                        />
                      </td>
                      <td className="!border !border-gray-400 px-1 py-0.5 text-center">
                        <input
                          type="text"
                          value={row.penalty_amount || ""}
                          onChange={(e) =>
                            handleViolationCell(rIdx, "penalty_amount", e.target.value)
                          }
                          className="!border-0 bg-transparent outline-none text-center w-full"
                        />
                      </td>
                      <td className="!border !border-gray-400 px-1 py-0.5">
                        <textarea
                          value={row.penalty_action || ""}
                          onChange={(e) =>
                            handleViolationCell(rIdx, "penalty_action", e.target.value)
                          }
                          rows={1}
                          className="!border-0 bg-transparent outline-none resize-none w-full text-[12px] text-center"
                        />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Vehicle Weight / Towing & Impound Fee table ── */}
          <div>
            <table className="!border-collapse text-[12px] w-[800px] mx-auto">
              <thead>
                <tr className="bg-yellow-200">
                  {["Vehicle Weight", "Towing Fee (Saudi Riyal/day)", "Daily Impound Fee (Saudi Riyal/day)"].map(
                    (h) => (
                      <th
                        key={h}
                        className="!border !border-gray-400 px-1.5 py-1 font-bold text-center"
                        style={{ width: "160px" }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {vehicleWeightTable.data.rows.map((row, rIdx) => (
                  <tr key={rIdx} className="bg-white">
                    <td className="!border !border-gray-400 px-1.5 py-1">
                      <input
                        type="text"
                        value={row.weight_class || ""}
                        onChange={(e) => handleVehicleWeightCell(rIdx, "weight_class", e.target.value)}
                        className="!border-0 bg-transparent outline-none w-full"
                      />
                    </td>
                    <td className="!border !border-gray-400 px-1.5 py-1 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {/* <span className="text-[14px]">⃁</span> */}
                        <input
                          type="text"
                          value={row.towing_fee || ""}
                          onChange={(e) => handleVehicleWeightCell(rIdx, "towing_fee", e.target.value)}
                          className="!border-0 bg-transparent outline-none text-center w-full"
                        />
                      </div>
                    </td>
                    <td className="!border !border-gray-400 px-1.5 py-1 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {/* <span className="text-[14px]">⃁</span> */}
                        <input
                          type="text"
                          value={row.impound_fee || ""}
                          onChange={(e) => handleVehicleWeightCell(rIdx, "impound_fee", e.target.value)}
                          className="!border-0 bg-transparent outline-none text-center w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── RIGHT: Assets Panel ── */}
        <AssetsPanel
          assetsTable={assetsTable}
          flatAssets={flatAssets}
          onSubCount={handleAssetSubCount}
          onTotalCount={handleAssetTotalCount}
        />
      </div>
    </div>
  );
};

export default CapitalParkingOnStreet;
